import { Event, ReporterBase } from '@/types'
import { getStateIndefiniteArticle, getStateName } from '@/utils/states'
import { ScriptCurrentDateParts } from '@/utils/time'

export const CA_INSTRUCTIONS_URL =
  'https://dbf-digital-reporters.helpscoutdocs.com/article/499-session-assistant-california-events'
export const CA_NOTICE_CHECKLIST_URL =
  'https://dbf-digital-reporters.helpscoutdocs.com/article/501-notice-checklist-for-ca'
export const WA_INSTRUCTIONS_URL =
  'https://dbf-digital-reporters.helpscoutdocs.com/article/525-session-assistant-washington-events'

export type ScriptLine = { type: 'say' | 'note'; text: string }

export type StateRecordScript = {
  stateName: string
  articleUrl: string
  verifiedOn: string
  crIntroduction: ScriptLine[] | null
  attendeesIntroduction: ScriptLine[]
  opening: ScriptLine[]
  questioning: ScriptLine[]
  closing: ScriptLine[]
}

const say = (text: string): ScriptLine => ({ type: 'say', text })
const note = (text: string): ScriptLine => ({ type: 'note', text })

const ATTENDEES_INTRODUCTION: ScriptLine[] = [
  note('Say "Session Assistant" if they explicitly say no videographer.'),
  say(
    'Good {partOfTheDay}, I’m {reporterName} and I am the [Videographer or Session Assistant] for today. Just to let you know, everything in the main room is being recorded for the duration of the session. However, breakout rooms are not recorded, so I would be happy to place you in a breakout room at any time when we’re off the record.',
  ),
  say(
    'While we’re waiting, could you please share your email in the Zoom chat?',
  ),
  note(
    'If counsel presses for an explanation or does not provide an email, state that we need it to send post-event documents.',
  ),
  say(
    'Thank you. Please let me know if I can assist with anything while we wait for everyone else to arrive.',
  ),
  note('If a breakout room is requested:'),
  say(
    'Counsel, there will be a button asking you to join the breakout room now. If you need anything, you can leave the breakout room at any time to come to the main room and I’ll be here to assist.',
  ),
  note(
    'CNA wait time: Do not inquire unless considerable time has elapsed. It is the role of the court reporter/deposition officer to inquire.',
  ),
]

const VIDEO_RECORDED_NOTE =
  'Leave out “video recorded” if the event was not noticed as video-recorded.'
const READ_ON =
  'Good {partOfTheDay}, we are now on the record. Today’s date is {dayOfWeek}, {date}, and the time is {time}. This is the [video recorded] [Volume #] {eventName} taken on behalf of the [Plaintiff/Defendant] in the matter of {caseName} filed in the [Full Case Venue], Case number {caseNumber}.'
const LOCATION =
  'This deposition is being held [Remotely/At Location Firm and Address if On-Site].'
const OPERATOR =
  'My name is {reporterName}, {jurisdictionArticle} {jurisdiction} notary public, and I will be acting as the videographer for today’s proceedings. I work with Depositions by Filevine, which is located at 1260 E. Stringham Avenue, Suite 600, Salt Lake City, Utah, 84106, and the phone number is (213) 632-5374.'
const STIPULATIONS =
  'Before we proceed, will the parties please state on the record any stipulations they wish to enter at this time?'
const IDENTIFY_COUNSEL =
  'Would all counsel please identify themselves for the record, spell their name, and identify the firm and party they represent?'
const HAND_OFF =
  'Thank you, the court reporter will now introduce themselves and swear in the witness.'
const CR_CONTROLS_RECORD_NOTE =
  'NOTE: The Court Reporter is now controlling the record and will perform any readbacks until a break is taken or the deposition ends.'

const QUESTIONING: ScriptLine[] = [
  note('Monitor timestamps.'),
  note(
    'If you take a break: Going off the record must be upon the agreement of ALL parties. Going on the record only takes ONE party, at which point you must read them back onto the record if the reporter is ready.',
  ),
  say('We are now OFF the record and the time is {time}.'),
  say('We are now ON the record and the time is {time}.'),
]

const closing = (reporterLabel: string): ScriptLine[] => [
  note(
    `Must close upon the agreement of ALL parties. The ${reporterLabel} should verify that no one else has any further questions. If the ${reporterLabel} does not do this, ask the parties if anyone has any further questioning.`,
  ),
  note(
    'If all parties are finished with their questioning and agree to go off:',
  ),
  say(
    "Before concluding the recording, will the parties please state on the record any stipulation they have concerning the custody of the audio/video recording and [if any] the exhibits introduced during today's deposition? Are there any other pertinent matters that need to be addressed?",
  ),
  say(
    'If there is nothing further, this concludes the video recorded {eventName}. The time is {time}, and we are going off the record.',
  ),
]

export const STATE_RECORD_SCRIPTS: Record<string, StateRecordScript> = {
  CA: {
    stateName: 'California',
    articleUrl: CA_INSTRUCTIONS_URL,
    verifiedOn: '2026-09-18',
    crIntroduction: null,
    attendeesIntroduction: [
      note(
        'Use this wording as a reference for educating the attendees on what to expect (this doesn’t need to be verbatim when off the record).',
      ),
      ...ATTENDEES_INTRODUCTION,
    ],
    opening: [
      note(VIDEO_RECORDED_NOTE),
      say(READ_ON),
      say(LOCATION),
      say(OPERATOR),
      say(STIPULATIONS),
      say(IDENTIFY_COUNSEL),
      note('Parties identify themselves.'),
      note('If remote:'),
      say(
        'For the record, do all parties stipulate to this deposition being conducted remotely and to me, acting as the videographer?',
      ),
      note('Parties stipulate.'),
      say(HAND_OFF),
      note(CR_CONTROLS_RECORD_NOTE),
    ],
    questioning: QUESTIONING,
    closing: closing('CSR'),
  },
  WA: {
    stateName: 'Washington',
    articleUrl: WA_INSTRUCTIONS_URL,
    verifiedOn: '2026-09-18',
    crIntroduction: [
      say(
        'Hello, I’m the Session Assistant here on behalf of Filevine. I’ll be managing the Zoom session and assisting with any relevant questions as needed. Just to confirm we are aligned before we start, that under Washington state rules for video depositions, I am required to perform the initial read-on to state my operator details for the record. Once I announce those details, I will hand it over to you to introduce yourself and swear in the witness. I will also announce when we go on and off the record today to allow you to focus fully on preparing the transcript.',
      ),
    ],
    attendeesIntroduction: ATTENDEES_INTRODUCTION,
    opening: [
      note(
        'Remember: Do NOT wait for the Court Reporter to ask you to read on. This is your responsibility as a Session Assistant.',
      ),
      note('Spotlight the witness on Zoom.'),
      note(
        'Go on the record and open the deposition: Try to speak clearly and do not rush when on the record.',
      ),
      say('Is everyone present ready to go on the record?'),
      note(VIDEO_RECORDED_NOTE),
      say(READ_ON),
      say(LOCATION),
      say(OPERATOR),
      say(STIPULATIONS),
      say(IDENTIFY_COUNSEL),
      note('Parties identify themselves.'),
      say(
        'For the record, do all parties stipulate to this deposition being conducted remotely (if applicable) and to me, acting as the videographer?',
      ),
      note('Parties stipulate.'),
      say(HAND_OFF),
      note(CR_CONTROLS_RECORD_NOTE),
    ],
    questioning: QUESTIONING,
    closing: closing('CR'),
  },
}

export const getStateRecordScript = (
  event: Pick<Event, 'case'>,
): StateRecordScript | null => STATE_RECORD_SCRIPTS[event.case.state] ?? null

export type ScriptValues = Record<string, string | null | undefined>

const FALLBACK_PLACEHOLDERS: Record<string, string> = {
  partOfTheDay: 'morning/afternoon',
  dayOfWeek: '[Day]',
  date: '[Date]',
  time: '[Time]',
  eventName: '[Event Name]',
  caseName: '[Case Name]',
  caseNumber: '[Case Number]',
  reporterName: '[Your Name]',
  jurisdictionArticle: 'a',
  jurisdiction: '[Jurisdiction]',
}

export const getScriptValues = (
  event: Pick<Event, 'name' | 'case'>,
  reporter: Pick<ReporterBase, 'name' | 'state'> | null | undefined,
  dt: ScriptCurrentDateParts,
): ScriptValues => ({
  partOfTheDay: dt.partOfTheDay,
  dayOfWeek: dt.dayOfWeek,
  date: dt.formattedDate,
  time: dt.formattedTimeWithTimezone,
  eventName: event.name,
  caseName: event.case.name,
  caseNumber: event.case.number,
  reporterName: reporter?.name,
  jurisdictionArticle:
    reporter?.state && getStateIndefiniteArticle(reporter.state),
  jurisdiction: reporter?.state && getStateName(reporter.state),
})

export const fillScript = (text: string, values: ScriptValues): string =>
  text.replace(
    /\{(\w+)\}/g,
    (match, token: string) =>
      values[token] || FALLBACK_PLACEHOLDERS[token] || match,
  )
